import './App.css';

import { ConfigProvider as ConfigProviderV4 } from 'antd';
import deDEv4 from 'antd/es/locale/de_DE';
import enUSv4 from 'antd/es/locale/en_US';
import esESv4 from 'antd/es/locale/es_ES';
import frFRv4 from 'antd/es/locale/fr_FR';
import itITv4 from 'antd/es/locale/it_IT';
import ptPTv4 from 'antd/es/locale/pt_PT';
import { ConfigProvider, notification } from 'antd5';
import deDE from 'antd5/locale/de_DE';
import enUS from 'antd5/locale/en_US';
import esES from 'antd5/locale/es_ES';
import frFR from 'antd5/locale/fr_FR';
import itIT from 'antd5/locale/it_IT';
import ptPT from 'antd5/locale/pt_PT';
import moment from 'moment-timezone';
import { lazy, Suspense as ReactSuspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { AcknowledgementModal } from 'components/AcknowledgementModal';
import PageNotFoundRedirect from 'components/PageNotFoundRedirect';
import { Portals } from 'components/Portals';
import { SideNav, useNavTabs } from 'components/SideNav';
import { Content, Layout, NotificationContextProvider, Spinner } from 'components/ui';
import { DashboardLanding } from 'horizon/components/CustomizableDashboard';
import { Banner, GlobalBannerContext } from 'horizon/components/GlobalBanner';
import { OrgMismatchModal } from 'horizon/components/OrgMismatch';
import OrgRedirect from 'horizon/components/OrgRedirect';
import {
  getReleaseNotificationConfig,
  shouldShowReleaseNotification,
} from 'horizon/components/ReleaseNotification';
import { RouteTitleManager } from 'horizon/components/RouteTitleManager/RouteTitleManager';
import { Home } from 'horizon/routes/Home';
import Reset from 'horizon/routes/Reset';
import { AtlasGqlAcknowledgementType, useGetAcknowledgementsQuery } from 'types/atlas-graphql';
import { WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';
import { useAccountContext } from 'utils/account/AccountContext';
import { useCustomerIdMismatch } from 'utils/account/hooks';
import { notifyBugsnag } from 'utils/bugsnag';
import { Theme } from 'utils/color/horizonTheme';
import { uuidRegex } from 'utils/constants';
import { isE2ETest } from 'utils/env';
import { useFeatures } from 'utils/features';
import { DAMAGES_DB_MAINTENANCE_BANNER, RELEASE_NOTIFICATION } from 'utils/release-toggles';
import { usePermissions } from 'utils/usePermissions';

const AlertsRoot = lazy(() => import('horizon/routes/Alerts/AlertsRoot'));
const ExecutiveDashboardRoot = lazy(
  () => import('horizon/routes/ExecutiveDashboard/ExecutiveDashboardRoot')
);
const ReportsRoot = lazy(() => import('horizon/routes/Reports/ReportsRoot'));
const AnalyticsRoot = lazy(() => import('horizon/routes/Analytics/AnalyticsRoot'));
const ApiExplorerRoot = lazy(() => import('horizon/routes/ApiExplorer/ApiExplorerRoot'));
const AssetDetails = lazy(() => import('horizon/components/Assets/AssetDetails'));
const DeviceFirmwareRoot = lazy(() => import('horizon/routes/DeviceFirmware/DeviceFirmwareRoot'));
const FleetRoot = lazy(() => import('horizon/routes/Fleet/FleetRoot'));
const InspectionsRoot = lazy(() => import('horizon/routes/Inspections/InspectionsRoot'));
const InspectionUploadTestHarness = lazy(
  () => import('horizon/routes/Inspections/InspectionUploadTestHarness')
);
const PicturesRoot = lazy(() => import('horizon/routes/Pictures/PicturesRoot'));
const ScheduleRoot = lazy(() => import('horizon/routes/Schedule/ScheduleRoot'));
const SettingsRoot = lazy(() => import('horizon/routes/Settings/SettingsRoot'));
const TransmissionsLineRoot = lazy(
  () => import('horizon/routes/TransmissionLines/TransmissionLinesRoot')
);
const UploadsRoot = lazy(() => import('horizon/routes/Uploads/UploadsRoot'));
const WorkOrdersRoot = lazy(() => import('horizon/routes/WorkOrders/WorkOrdersRoot'));

// antd 4 renders 2px corners, and the app still mixes both versions, so antd 5 controls and
// tables match them until the migration is done (docs/antd5-migration-plan.md, 9.1).
const ANTD4_RADIUS = { borderRadius: 2, borderRadiusLG: 2, borderRadiusSM: 2 };
// App.css scopes its antd 5 tooltip arrow fix to this class.
const ANTD5_TOOLTIP_CLASS_NAME = 'antd5-tooltip';

let previousLocation;

function SideNavWrapper() {
  const tabs = useNavTabs();
  const { pathname } = useLocation();

  if (pathname.startsWith('/reset')) {
    // when resetting, we show a loading screen, and don't want to show
    // page-level navigation
    return null;
  }

  return <SideNav tabs={tabs} />;
}

function Suspense({ children }) {
  return <ReactSuspense fallback={<Spinner />}>{children}</ReactSuspense>;
}

export default function App() {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const useModal = location.state?.useModal ?? false;
  const [globalBannerMessage, setGlobalBannerMessage] = useState();
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);

  // Map i18n language codes to Ant Design locales
  const getAntdLocale = () => {
    switch (i18n.language) {
      case 'es':
        return esES;
      case 'fr':
        return frFR;
      case 'de':
        return deDE;
      case 'pt':
        return ptPT;
      case 'it':
        return itIT;
      case 'en':
      default:
        return enUS;
    }
  };

  // antd v4 (legacy) Form validation messages and Select empty text need their own
  // ConfigProvider locale; the antd5 ConfigProvider above does not localize them.
  const getAntdV4Locale = () => {
    switch (i18n.language) {
      case 'es':
        return esESv4;
      case 'fr':
        return frFRv4;
      case 'de':
        return deDEv4;
      case 'pt':
        return ptPTv4;
      case 'it':
        return itITv4;
      case 'en':
      default:
        return enUSv4;
    }
  };
  const [customerIdMismatch] = useCustomerIdMismatch();
  const { hasReleaseToggle } = useAccountContext();
  const { features, loading: featuresLoading } = useFeatures();
  const { UPLOADS } = features;
  const shouldRegisterUploadsRoute = featuresLoading || UPLOADS;
  const hasReleaseNotificationReleaseToggle = hasReleaseToggle(RELEASE_NOTIFICATION);
  const hasMaintenanceBanner = hasReleaseToggle(DAMAGES_DB_MAINTENANCE_BANNER);
  const [notificationApi, notificationContext] = notification.useNotification();

  /** Temporary banner for DB Upgrade */
  useEffect(() => {
    if (hasMaintenanceBanner && !globalBannerMessage) {
      const maintenanceTime = moment
        .tz('2026-03-26 18:00', 'America/New_York')
        .tz(moment.tz.guess());
      setGlobalBannerMessage(
        `Scheduled maintenance ${maintenanceTime.format('MMMM Do, h:mm a z')}. Brief downtime may be experienced.`
      );
    }
  }, [hasMaintenanceBanner, globalBannerMessage]);

  const { data, error: acknowledgementsError } = useGetAcknowledgementsQuery({
    variables: {
      acknowledgementType: AtlasGqlAcknowledgementType.Gdpr,
    },
  });

  useEffect(() => {
    if (data?.acknowledgements.length > 0) {
      setShowAcknowledgeModal(true);
    }
  }, [data]);

  useEffect(() => {
    if (acknowledgementsError) {
      notifyBugsnag(`GetAcknowledgements error: ${acknowledgementsError.message}`);
    }
  }, [acknowledgementsError]);

  const acknowledgementsByUser = data?.acknowledgements ?? {};

  const { hasRole } = usePermissions();
  const isWoCollaborator = hasRole(WORK_ORDER_COLLABORATOR);

  useEffect(() => {
    if (history.action === 'REPLACE' || useModal) {
      return;
    }

    previousLocation = location;
  }, [location, history, useModal]);

  // should only show once when app is loaded (opened in a new tab, refreshed, etc.)
  useEffect(() => {
    if (hasReleaseNotificationReleaseToggle && shouldShowReleaseNotification()) {
      notificationApi.open(getReleaseNotificationConfig());
    }
  }, [hasReleaseNotificationReleaseToggle, notificationApi]);

  const fullRoutes = (
    <Switch location={useModal && previousLocation ? previousLocation : location}>
      <Route
        exact={true}
        path="/"
        render={props => {
          // handle old "hash" routes with a simple redirect
          const { hash } = props.location;
          return hash ? (
            <Redirect to={hash.replace('#', '')} />
          ) : (
            <DashboardLanding fallback={<Home {...props} />} />
          );
        }}
      />

      <Route
        path="/analytics"
        render={() => (
          <Suspense>
            <AnalyticsRoot />
          </Suspense>
        )}
      />

      <Route
        path="/alerts"
        render={() => (
          <Suspense>
            <AlertsRoot />
          </Suspense>
        )}
      />
      <Route
        path={['/transmission-lines', '/transmission-line-uploads']}
        render={() => (
          <Suspense>
            <TransmissionsLineRoot />
          </Suspense>
        )}
      />

      <Route
        path="/settings"
        render={() => (
          <Suspense>
            <SettingsRoot />
          </Suspense>
        )}
      />

      <Route
        path="/device-firmware-management"
        render={() => (
          <Suspense>
            <DeviceFirmwareRoot />
          </Suspense>
        )}
      />

      <Route
        path="/executive-dashboard"
        render={() => (
          <Suspense>
            <ExecutiveDashboardRoot />
          </Suspense>
        )}
      />

      <Route
        path="/fleet"
        render={() => (
          <Suspense>
            <FleetRoot />
          </Suspense>
        )}
      />

      {shouldRegisterUploadsRoute ? (
        <Route
          path="/uploads"
          render={() => (
            <Suspense>
              <UploadsRoot />
            </Suspense>
          )}
        />
      ) : null}

      <Route
        path={['/damages', '/damages2', '/inspections']}
        render={() => (
          <Suspense>
            <InspectionsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/reports', '/report-deliveries']}
        render={() => (
          <Suspense>
            <ReportsRoot />
          </Suspense>
        )}
      />

      <Route
        path={[`/sites/:id${uuidRegex}`, `/turbines/:id${uuidRegex}`, `/blades/:id${uuidRegex}`]}
        render={({ location }) => {
          const assetsRoute = `/${['assets', ...location.pathname.split('/').slice(2)].join('/')}`;

          return <Redirect to={assetsRoute} />;
        }}
      />

      <Route
        path={[`/assets/:id${uuidRegex}`]}
        render={() => (
          <Suspense>
            <AssetDetails />
          </Suspense>
        )}
      />

      <Route
        path={[
          '/work-order-create',
          '/work-orders',
          '/campaigns',
          '/unplanned-tasks',
          '/tasks',
          '/recurring-task',
        ]}
        render={() => (
          <Suspense>
            <WorkOrdersRoot />
          </Suspense>
        )}
      />

      <Route
        exact={true}
        path="/schedule"
        render={() => (
          <Suspense>
            <ScheduleRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/pictures', '/picture-review']}
        render={() => (
          <Suspense>
            <PicturesRoot />
          </Suspense>
        )}
      />

      <Route exact={true} path="/reset" component={Reset} />

      {isE2ETest && (
        <Route
          exact={true}
          path="/test/inspection-upload"
          render={() => (
            <Suspense>
              <InspectionUploadTestHarness />
            </Suspense>
          )}
        />
      )}

      <Route
        path="/api/:apiName"
        render={() => (
          <Suspense>
            <ApiExplorerRoot />
          </Suspense>
        )}
      />

      <Route path="/o/:orgId" render={() => <OrgRedirect />} />
      <Route component={PageNotFoundRedirect} />
    </Switch>
  );

  const woCollaboratorRoutes = (
    <Switch location={useModal && previousLocation ? previousLocation : location}>
      <Route
        exact={true}
        path="/"
        render={props => {
          // handle old "hash" routes with a simple redirect
          const { hash } = props.location;
          // Work Order Collaborators never land on the customizable dashboard — per
          // product (sc-162103) they go straight to their Work Orders, regardless of
          // the org-wide `customizable_dashboard` toggle.
          return hash ? <Redirect to={hash.replace('#', '')} /> : <Redirect to={'/work-orders'} />;
        }}
      />

      <Route
        path="/settings"
        render={() => (
          <Suspense>
            <SettingsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/work-orders', '/campaigns', '/unplanned-tasks', '/tasks']}
        render={() => (
          <Suspense>
            <WorkOrdersRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/reports', '/report-deliveries']}
        render={() => (
          <Suspense>
            <ReportsRoot />
          </Suspense>
        )}
      />

      <Route
        path={[`/inspections/:id${uuidRegex}`]}
        render={() => (
          <Suspense>
            <InspectionsRoot />
          </Suspense>
        )}
      />

      <Route
        path={['/pictures']}
        render={() => (
          <Suspense>
            <PicturesRoot />
          </Suspense>
        )}
      />

      {shouldRegisterUploadsRoute ? (
        <Route
          path="/uploads"
          render={() => (
            <Suspense>
              <UploadsRoot />
            </Suspense>
          )}
        />
      ) : null}

      <Route exact={true} path="/reset" component={Reset} />

      <Route
        path="/api/:apiName"
        render={() => (
          <Suspense>
            <ApiExplorerRoot />
          </Suspense>
        )}
      />

      <Route path="/o/:orgId" render={() => <OrgRedirect />} />
      <Route component={PageNotFoundRedirect} />
    </Switch>
  );

  return (
    <ConfigProviderV4 locale={getAntdV4Locale()}>
      <ConfigProvider
        locale={getAntdLocale()}
        tooltip={{ className: ANTD5_TOOLTIP_CLASS_NAME }}
        /**
         * Antd 5's primary color and typography follow the active styled-components
         * Horizon theme.
         *
         * All other theme config is through styled-components
         * <ThemeProvider /> in ./index.tsx. See `src/utils/color/THEME.md`
         * for detail.
         */
        theme={{
          token: {
            colorPrimary: theme.teal,
            // Links use the deeper, legible action-text color rather than the
            // bright Sky Blue fill color, so inline text stays readable on light.
            colorLink: theme.actionText,
            colorLinkHover: theme.actionTextHover,
            fontFamily: 'Inter, sans-serif',
            borderRadius: 8,
          },
          // Solid primary buttons use navy text on the Sky Blue fill so labels
          // stay legible.
          components: {
            Button: {
              ...ANTD4_RADIUS,
              // antd 4's icon-only sizes; antd 5 inherits the label size.
              onlyIconSize: 16,
              onlyIconSizeLG: 18,
              onlyIconSizeSM: 14,
              primaryColor: theme.solidActionText,
            },
            Checkbox: ANTD4_RADIUS,
            // antd's in-range and selected-time fill is near-white, which hides the dark theme's
            // light cell text. With no darkAlgorithm, the dark panel's text, dividers and range
            // arrow come from here too.
            DatePicker: {
              ...ANTD4_RADIUS,
              controlItemBgActive: theme.selectedListItemBackground,
              ...(theme.themeName === Theme.Dark && {
                colorBgElevated: theme.tertiaryBackgroundColor,
                colorSplit: theme.borderColor,
                colorText: theme.textColor,
              }),
            },
            // antd's 45% black submenu arrow vanishes on the dark theme's navy.
            Dropdown: { colorIcon: theme.secondaryTextColor },
            Input: ANTD4_RADIUS,
            InputNumber: ANTD4_RADIUS,
            // Menus render inside the app's square dropdowns, so they drop antd's rounded rows and
            // the vertical menu's right border.
            Menu: {
              activeBarBorderWidth: 0,
              itemBorderRadius: 4,
              subMenuItemBorderRadius: 4,
              itemColor: theme.headerColor,
              itemHoverBg: theme.activeMenuItemBackgroundColor,
            },
            Radio: ANTD4_RADIUS,
            // antd's light track and selected fill sit under the dark theme's light labels.
            Segmented:
              theme.themeName === Theme.Dark
                ? {
                    trackBg: theme.secondaryBackgroundColor,
                    itemColor: theme.labelColor,
                    itemHoverColor: theme.headerColor,
                    itemHoverBg: theme.tertiaryBackgroundColor,
                    itemActiveBg: theme.tertiaryBackgroundColor,
                    itemSelectedBg: theme.selectedListItemBackground,
                    itemSelectedColor: theme.headerColor,
                  }
                : {},
            Select: ANTD4_RADIUS,
            Table: {
              // antd 4's corners and 500-weight headers with no dividers, until the migration is
              // done (docs/antd5-migration-plan.md, 9.1).
              ...ANTD4_RADIUS,
              fontWeightStrong: 500,
              headerSplitColor: 'transparent',
              // With no darkAlgorithm, a dark-theme Table keeps antd's white surfaces under the
              // dark theme's light text. DataTable styles its own; this covers the plain Tables.
              ...(theme.themeName === Theme.Dark && {
                colorBgContainer: theme.secondaryBackgroundColor,
                colorText: theme.headerColor,
                colorTextHeading: theme.labelColor,
                colorBorderSecondary: theme.borderColor,
                colorIcon: theme.tableHeaderButtonColor,
                colorIconHover: theme.labelColor,
                headerBg: theme.tertiaryBackgroundColor,
                headerSortActiveBg: theme.secondaryBackgroundColor,
                headerSortHoverBg: theme.secondaryBackgroundColor,
                fixedHeaderSortActiveBg: theme.secondaryBackgroundColor,
                bodySortBg: theme.tertiaryBackgroundColor,
                rowHoverBg: theme.tertiaryBackgroundColor,
                rowExpandedBg: theme.tertiaryBackgroundColor,
                footerBg: theme.tertiaryBackgroundColor,
              }),
            },
            // antd's default rail is 4% black, which disappears on the dark theme's navy, and its
            // disabled track is fainter still.
            Slider: {
              railBg: theme.borderColor,
              railHoverBg: theme.borderColor,
              trackBg: theme.teal,
              trackHoverBg: theme.teal,
              trackBgDisabled: theme.secondaryTextColor,
              handleColor: theme.teal,
              colorPrimaryBorderHover: theme.teal,
              dotActiveBorderColor: theme.teal,
            },
            Tabs: {
              colorBorderSecondary: theme.borderColor,
              itemColor: theme.headerColor,
              itemSelectedColor: theme.actionText,
              itemHoverColor: theme.actionText,
              itemActiveColor: theme.actionTextHover,
            },
            TreeSelect: ANTD4_RADIUS,
            // antd's light-palette text, description and status colors are near-black or too dark
            // on the dark theme's navy.
            Typography:
              theme.themeName === Theme.Dark
                ? {
                    colorText: theme.textColor,
                    colorTextDescription: theme.secondaryTextColor,
                    colorTextDisabled: theme.disabledTextColor,
                    colorErrorText: theme.red,
                    colorSuccessText: theme.green,
                    colorWarningText: theme.yellow,
                    colorIcon: theme.secondaryTextColor,
                  }
                : {},
            // antd's 45% black file and delete icons vanish on the dark theme's navy.
            Upload: { colorIcon: theme.secondaryTextColor },
          },
        }}
      >
        <NotificationContextProvider>
          <GlobalBannerContext.Provider
            value={{ message: globalBannerMessage, setMessage: setGlobalBannerMessage }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100dvh',
                maxHeight: '100dvh',
              }}
            >
              {globalBannerMessage && <Banner message={globalBannerMessage} />}
              <Layout style={{ flexGrow: 1 }}>
                <AcknowledgementModal
                  showModal={showAcknowledgeModal}
                  setShowModal={setShowAcknowledgeModal}
                  acknowledgements={acknowledgementsByUser}
                />
                {customerIdMismatch ? <OrgMismatchModal /> : null}
                {notificationContext}
                <RouteTitleManager />
                <SideNavWrapper />
                {/* This classname is the same as the constant APP_SCROLL_CONTAINER_CLASSNAME but has to be a string literal here for the use-case to work properly */}
                <Layout className="scrollable-app-content-wrapper" style={{ overflowY: 'scroll' }}>
                  <Content>
                    {isWoCollaborator ? woCollaboratorRoutes : fullRoutes}

                    {/* Not sure why there's a separate Switch... */}
                    {useModal && previousLocation ? (
                      <Switch>
                        <Route
                          path={['/pictures', '/picture-review']}
                          render={() => (
                            <Suspense>
                              <PicturesRoot />
                            </Suspense>
                          )}
                        />

                        <Route
                          path="/transmission-lines"
                          render={() => (
                            <Suspense>
                              <TransmissionsLineRoot />
                            </Suspense>
                          )}
                        />
                      </Switch>
                    ) : null}
                  </Content>

                  <Portals />
                </Layout>
              </Layout>
            </div>
          </GlobalBannerContext.Provider>
        </NotificationContextProvider>
      </ConfigProvider>
    </ConfigProviderV4>
  );
}
